import { Injectable } from '@angular/core';
import { SelectOptionEntity, ResponseValueObject } from '@gishub/domain';
import { BaseNotifier } from '../../../../../shared/notifiers/base-notifier';
import { Ref } from '../../../../../shared/providers/ref';
import { UbigeoState } from './ubigeo-state';
import { UbigeoRepositoryProvider } from '../../../../../shared/providers/repository-providers';
import { SELECTED_UBIGEO_LAYER_ID } from '../../map-highlight-layers';
import { MapProvider } from '../../../components/map/map-provider';
import { FullExtentProvider } from '../../../components/full-extent/full-extent-provider';
import GraphicsLayer from '@arcgis/core/layers/GraphicsLayer';
import Graphic from '@arcgis/core/Graphic';
import Polygon from '@arcgis/core/geometry/Polygon';
import SimpleFillSymbol from '@arcgis/core/symbols/SimpleFillSymbol';
import SimpleLineSymbol from '@arcgis/core/symbols/SimpleLineSymbol';
import Color from '@arcgis/core/Color';
import * as webMercatorUtils from '@arcgis/core/geometry/support/webMercatorUtils';

/**
 * Cascada ubigeo (Departamento → Provincia → Distrito).
 *
 * Robustez:
 * - Token anti-carrera: cualquier selección nueva invalida los fetches en
 *   vuelo de la anterior (ni estados ni geometrías tardías pisan la selcción
 *   vigente).
 * - El highlight del mapa SIEMPRE corresponde a la selección vigente: si la
 *   geometría falla o viene vacía, se limpia el boundary anterior.
 * - view.goTo abortado (zoom/cambio de vista) no rompe el flujo: se ignora.
 */
@Injectable()
export class UbigeoNotifier extends BaseNotifier<UbigeoState> {
  /** Token anti-carrera de la cascada (selecciones + fetches de geometría). */
  private _selToken = 0;

  /** Geometría pendiente mientras el mapa no está listo (con su token). */
  private _pendingData: { token: number; data: any } | null = null;

  constructor(ref: Ref) {
    super(ref, {
      departments: [],
      selectedDepartment: null,
      selectedDepartmentName: '',
      selectedProvinceName: '',
      selectedDistrictName: '',
      provinces: [],
      selectedProvince: null,
      districts: [],
      selectedDistrict: null,
      currentGeometry: null,
    });

    this.listen(() => {
      const mapState = this.ref.watch(MapProvider).state();
      if (mapState.view && mapState.map && this._pendingData) {
        const pending = this._pendingData;
        this._pendingData = null;
        // Solo aplica si sigue siendo la última petición.
        if (pending.token === this._selToken) {
          void this.processGeometryData(pending.data);
        }
      }
    });
  }

  private get ubigeoRepo() {
    return this.ref.read(UbigeoRepositoryProvider).instance;
  }

  private get mapProvider() {
    return this.ref.read(MapProvider);
  }

  override async build(): Promise<void> {
    await this.loadDepartments();
  }

  async selectDepartment(code: string): Promise<void> {
    // Toda selección nueva invalida fetches en vuelo (cascada y geometría).
    const token = ++this._selToken;

    if (!code) {
      this.setState({
        ...this.snapshot(),
        selectedDepartment: null,
        selectedDepartmentName: 'Nacional',
        selectedProvince: null,
        selectedProvinceName: '',
        provinces: [],
        selectedDistrict: null,
        selectedDistrictName: '',
        districts: [],
        currentGeometry: null,
      });
      this.ref.watch(FullExtentProvider).notifier.setGeometry(null);
      this.clearHighlight();

      const mapState = this.mapProvider.state();
      if (mapState.view) {
        mapState.view.constraints = {};
        this.safeGoTo(mapState.view.goTo({ target: [-75.0, -9.5], zoom: 6 }, { animate: true, duration: 1000 }));
      }
      return;
    }

    const dept = this.snapshot().departments.find(d => d.value === code);
    this.setState({
      ...this.snapshot(),
      selectedDepartment: code,
      selectedDepartmentName: dept?.label ?? '',
      selectedProvince: null,
      selectedProvinceName: '',
      provinces: [],
      selectedDistrict: null,
      selectedDistrictName: '',
      districts: [],
      currentGeometry: null,
    });
    this.ref.watch(FullExtentProvider).notifier.setGeometry(null);
    await this.loadProvinces(token, code);
    if (token !== this._selToken) return;

    await this.fetchAndApplyGeometry(token, () => this.ubigeoRepo.getDepartmentGeometry(code), {
      constraint: true,
    });
  }

  async selectProvince(code: string, name?: string): Promise<void> {
    const token = ++this._selToken;
    const prov = this.snapshot().provinces.find(p => p.value === code);
    this.setState({
      ...this.snapshot(),
      selectedProvince: code || null,
      selectedProvinceName: prov?.label ?? name ?? '',
      selectedDistrict: null,
      selectedDistrictName: '',
      districts: [],
      currentGeometry: null,
    });
    this.ref.watch(FullExtentProvider).notifier.setGeometry(null);

    if (code) {
      await this.loadDistricts(token, code);
      if (token !== this._selToken) return;
      await this.fetchAndApplyGeometry(token, () => this.ubigeoRepo.getProvinceGeometry(code));
    } else {
      // "Toda la Región": vuelve a la geometría del departamento activo.
      const deptCode = this.snapshot().selectedDepartment;
      if (deptCode) {
        await this.fetchAndApplyGeometry(token, () => this.ubigeoRepo.getDepartmentGeometry(deptCode), {
          constraint: true,
        });
      }
    }
  }

  async selectDistrict(code: string, name?: string): Promise<void> {
    const token = ++this._selToken;
    const dist = this.snapshot().districts.find(d => d.value === code);
    this.setState({
      ...this.snapshot(),
      selectedDistrict: code || null,
      selectedDistrictName: dist?.label ?? name ?? '',
      currentGeometry: null,
    });
    this.ref.watch(FullExtentProvider).notifier.setGeometry(null);

    if (code) {
      await this.fetchAndApplyGeometry(token, () => this.ubigeoRepo.getDistrictGeometry(code));
    } else {
      // "Todos los Distritos": vuelve a la geometría de la provincia activa.
      const provCode = this.snapshot().selectedProvince;
      if (provCode) {
        await this.fetchAndApplyGeometry(token, () => this.ubigeoRepo.getProvinceGeometry(provCode));
      }
    }
  }

  highlightOnMap(esriJson: any): void {
    const mapState = this.mapProvider.state();
    if (!mapState.view || !mapState.map) {
      this._pendingData = { token: this._selToken, data: esriJson };
      return;
    }
    void this.processGeometryData(esriJson);
  }

  /** Quita el boundary del mapa y cancela geometrías pendientes. */
  private clearHighlight(): void {
    this._pendingData = null;
    const mapState = this.mapProvider.state();
    if (!mapState.map) return;
    const layer = mapState.map.findLayerById(SELECTED_UBIGEO_LAYER_ID) as GraphicsLayer;
    if (layer) layer.removeAll();
  }

  /** view.goTo promete rechazar cuando el usuario interrumpe: ignorarlo. */
  private safeGoTo(promise: Promise<any> | undefined): void {
    promise?.catch(() => { /* navegación interrumpida: no es error */ });
  }

  /**
   * Fetch de geometría con token + aplicación atómica: estado.currentGeometry,
   * FullExtent, constraint (solo departamento) y highlight del mapa. Si falla
   * o viene vacía, limpia el highlight anterior para no mostrar otro ámbito.
   */
  private async fetchAndApplyGeometry(
    token: number,
    fetch: () => Promise<ResponseValueObject<any>>,
    opts: { constraint?: boolean } = {},
  ): Promise<void> {
    try {
      const geomRes = await fetch();
      if (token !== this._selToken) return; // llegó tarde: descartar

      const features = geomRes?.data?.features ?? [];
      if (!geomRes.success || features.length === 0) {
        console.warn('[Ubigeo] Geometría no disponible para la selección activa');
        this.clearHighlight();
        return;
      }

      this._setGeometryFromResponse(geomRes.data);
      if (opts.constraint) this._setDepartmentConstraint(geomRes.data);
      this.highlightOnMap(geomRes.data);
    } catch (ex) {
      if (token !== this._selToken) return;
      console.error('[Ubigeo] Error fetching geometry', ex);
      this.clearHighlight();
    }
  }

  private _setGeometryFromResponse(esriJson: any): void {
    let geometry = esriJson?.features?.[0]?.geometry ?? null;
    if (geometry && !geometry.spatialReference && esriJson?.spatialReference) {
      geometry.spatialReference = esriJson.spatialReference;
    }
    geometry = geometry ? Polygon.fromJSON(geometry) : null;
    if (geometry?.spatialReference?.isGeographic) {
      geometry = webMercatorUtils.geographicToWebMercator(geometry);
    }
    this.setState({ ...this.snapshot(), currentGeometry: geometry });
    this.ref.watch(FullExtentProvider).notifier.setGeometry(geometry);
  }

  private _setDepartmentConstraint(esriJson: any): void {
    const view = this.mapProvider.state().view;
    if (!view) return;

    const raw = esriJson?.features?.[0]?.geometry ?? null;
    if (!raw) return;
    try {
      const spatialRef = esriJson.spatialReference || raw.spatialReference;
      let polygon = Polygon.fromJSON({ ...raw, spatialReference: spatialRef });
      if (polygon.spatialReference?.isGeographic) {
        polygon = webMercatorUtils.geographicToWebMercator(polygon) as Polygon;
      }
      const extent = polygon.extent;
      if (extent) {
        view.constraints = { geometry: extent.expand(1.3), minZoom: 7, maxZoom: 19 };
      }
    } catch (ex) {
      console.warn('[Ubigeo] Error setting extent constraint', ex);
    }
  }

  private async processGeometryData(esriJson: any): Promise<void> {
    const mapState = this.mapProvider.state();
    const map = mapState.map;
    const view = mapState.view;

    if (!map || !view || !esriJson?.features || esriJson.features.length === 0) return;

    let layer = map.findLayerById(SELECTED_UBIGEO_LAYER_ID) as GraphicsLayer;
    if (!layer) {
      layer = new GraphicsLayer({
        id: SELECTED_UBIGEO_LAYER_ID,
        listMode: 'hide',
        effect: 'bloom(1.5, 0.5px, 0.1) drop-shadow(2px, 2px, 4px, rgba(0,0,0,0.6))',
      });
      map.add(layer);
    }
    layer.removeAll();

    // Resaltado inequívoco: relleno suave + contorno grueso. El usuario
    // debe ver la unidad seleccionada sin buscarla.
    const symbol = new SimpleFillSymbol({
      color: new Color([0, 200, 255, 0.14]),
      outline: new SimpleLineSymbol({ color: new Color([0, 200, 255, 1]), width: 2.5, style: 'solid' }),
    });

    const globalSR = esriJson.spatialReference;
    const graphics: Graphic[] = [];
    for (const feature of esriJson.features) {
      if (!feature?.geometry) continue;
      const geomData = { ...feature.geometry };
      if (!geomData.spatialReference && globalSR) geomData.spatialReference = globalSR;
      try {
        graphics.push(new Graphic({ geometry: Polygon.fromJSON(geomData), symbol }));
      } catch (ex) {
        console.warn('[Ubigeo] Geometría inválida omitida', ex);
      }
    }
    layer.addMany(graphics);

    if (graphics.length > 0) {
      let extent = graphics[0].geometry?.extent ?? null;
      graphics.forEach((g: Graphic) => {
        if (g.geometry?.extent) extent = extent ? extent.union(g.geometry.extent) : g.geometry.extent;
      });
      if (extent) {
        this.safeGoTo(view.goTo(extent.expand(1.2), { animate: true, duration: 1000, easing: 'ease-in-out' }));
      }
    }
  }

  private async loadDepartments(): Promise<void> {
    try {
      const res = await this.ubigeoRepo.getDepartments();
      const departments: SelectOptionEntity[] = [
        { label: 'Nacional', value: '' },
        ...(res.data ?? []).map(d => ({ label: d.name, value: d.code })),
      ];
      this.setState({ ...this.snapshot(), departments });
    } catch (ex) {
      console.error('[Ubigeo] Error loading departments', ex);
    }
  }

  private async loadProvinces(token: number, departmentCode: string): Promise<void> {
    try {
      const res = await this.ubigeoRepo.getProvincesByDepartment(departmentCode);
      if (token !== this._selToken) return; // cascada obsoleta: descartar
      const provinces: SelectOptionEntity[] = [
        { label: 'Toda la Región', value: '' },
        ...(res.data ?? []).map(p => ({ label: p.name, value: p.code })),
      ];
      this.setState({ ...this.snapshot(), provinces });
    } catch (ex) {
      if (token !== this._selToken) return;
      console.error('[Ubigeo] Error loading provinces', ex);
    }
  }

  private async loadDistricts(token: number, provinceCode: string): Promise<void> {
    try {
      const res = await this.ubigeoRepo.getDistrictsByProvince(provinceCode);
      if (token !== this._selToken) return; // cascada obsoleta: descartar
      const districts: SelectOptionEntity[] = [
        { label: 'Todos los Distritos', value: '' },
        ...(res.data ?? []).map(d => ({ label: d.name, value: d.code })),
      ];
      this.setState({ ...this.snapshot(), districts });
    } catch (ex) {
      if (token !== this._selToken) return;
      console.error('[Ubigeo] Error loading districts', ex);
    }
  }
}
